import classNames from 'classnames'
import type {
  ButtonHTMLAttributes,
  FC,
  MouseEventHandler,
  ReactNode,
} from 'react'

import {RenderIf} from '../Basic/RenderIf/RenderIf'

type CommonAnnouncementPillProps = {
  /** Text rendered inside the pill. */
  label: string
  /** Allow callers to extend layout related styles. */
  className?: string
  /**
   * Static pills use this as the accessible name on a `role="status"` container;
   * interactive pills use it as the button’s accessible name.
   */
  'aria-label': string
}

type InteractiveAnnouncementPillProps = CommonAnnouncementPillProps & {
  onClick: MouseEventHandler<HTMLButtonElement>
  type?: 'button' | 'submit' | 'reset'
} & Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'type' | 'children' | 'className' | 'aria-label'
  >

export type AnnouncementPillProps =
  | CommonAnnouncementPillProps
  | InteractiveAnnouncementPillProps

function isInteractivePill(
  props: AnnouncementPillProps,
): props is InteractiveAnnouncementPillProps {
  return 'onClick' in props && typeof props.onClick === 'function'
}

type InteractivePillButtonProps = InteractiveAnnouncementPillProps & {
  pillClassName: string
  layers: ReactNode
}

const InteractivePillButton: FC<InteractivePillButtonProps> = ({
  pillClassName,
  layers,
  label: _label,
  className: _className,
  onClick,
  type = 'button',
  'aria-label': ariaLabel,
  ...buttonHtmlProps
}) => (
  <button
    {...buttonHtmlProps}
    type={type}
    className={pillClassName}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    {layers}
  </button>
)

export const AnnouncementPill: FC<AnnouncementPillProps> = props => {
  const {label, className, 'aria-label': ariaLabel} = props
  const interactive = isInteractivePill(props)

  const pillClassName = classNames(
    'announcement-pill',
    {'announcement-pill--static': !interactive},
    className,
  )

  const layers = (
    <>
      <span className="announcement-pill__layer announcement-pill__layer--base">
        {label}
      </span>
      <span
        aria-hidden="true"
        className="announcement-pill__layer announcement-pill__layer--blur"
      >
        {label}
      </span>
      <span
        aria-hidden="true"
        className="announcement-pill__layer announcement-pill__layer--ghost shine"
      >
        {label}
      </span>
    </>
  )

  return (
    <>
      <RenderIf isTrue={interactive}>
        <InteractivePillButton
          {...(props as InteractiveAnnouncementPillProps)}
          pillClassName={pillClassName}
          layers={layers}
        />
      </RenderIf>
      <RenderIf isTrue={!interactive}>
        <div
          role="status"
          aria-live="polite"
          className={pillClassName}
          aria-label={ariaLabel}
        >
          {layers}
        </div>
      </RenderIf>
    </>
  )
}
